import { Card, Colors, LevelTrendChart, LineChart, Pills, Spinner, Text } from '@amelio/ui-library';

import { HEADLINE_CARD_SURFACE, HEADLINE_CARD_TITLE } from '../headlineCard';
import {
    TREND_SCORE_SCALE,
    toReachedBandId,
    toReachedLevelLabels,
    toTrendBands,
    toTrendChartPoints,
} from './trendChartPoints';

import type { TranslationKey } from '@/i18n-key';
import type { LineChartPoint } from '@amelio/ui-library';
import type { ReactNode } from 'react';

import { useGetPerformanceScoreLevels } from '@/api/competency-services/performance-score-level';
import { useGetEvaluationPeriods } from '@/api/cycle-evaluation-services/manager-assessment';
import { useGetPerformanceTrend } from '@/api/performance-profile';
import { useAmelioTranslations } from '@/hooks';

/**
 * The shape the example draws: three cycles going up. The scores live here and nowhere else -- no
 * path joins them to a server -- and none of them is ever printed, on a mark or on an axis.
 */
const EXAMPLE_CYCLES: { score: number; labelKey: TranslationKey }[] = [
    { score: 38, labelKey: 'performanceProfile.performanceTrend.example.firstCycle' },
    { score: 61, labelKey: 'performanceProfile.performanceTrend.example.secondCycle' },
    { score: 84, labelKey: 'performanceProfile.performanceTrend.example.thirdCycle' },
];

interface PerformanceTrendChartProps {
    /** The employee whose cycles are read; `undefined` keeps the card from asking the server. */
    employeeId?: string;
    className?: string;
}

interface TrendCardProps {
    className?: string;
    children: ReactNode;
}

/** Title and surface shared by every state, so the card never changes shape between them. */
const TrendCard = ({ className, children }: TrendCardProps) => {
    const { t } = useAmelioTranslations();

    return (
        <Card
            borderRadius={'3xl'}
            bgColor={'bg-white/60'}
            className={`${HEADLINE_CARD_SURFACE} flex flex-col ${className ?? ''}`}
            dataTestId={'performance-trend-card'}
        >
            <h3 className={HEADLINE_CARD_TITLE}>
                {t('performanceProfile.performanceTrend.title')}
            </h3>
            {/* What the card has left under its title, whatever the neighbour's height makes that:
                the chart fills it, so the tile never ends on a blank band. */}
            <div className={'flex min-h-0 flex-1 flex-col justify-center'}>{children}</div>
        </Card>
    );
};

interface TrendNoticeProps {
    badgeKey: TranslationKey;
    sentenceKey: TranslationKey;
    className?: string;
}

/** What the card says when it has no series to draw: a word for the case, then the sentence. */
const TrendNotice = ({ badgeKey, sentenceKey, className }: TrendNoticeProps) => {
    const { t } = useAmelioTranslations();

    return (
        <div
            className={`flex flex-col items-center justify-center gap-(--gap-gap-md) ${className ?? ''}`}
        >
            <Pills
                variant={'default'}
                size={'sm'}
            >
                {t(badgeKey)}
            </Pills>
            <Text
                color={Colors.NAVY}
                align={'center'}
                className={'text-body-sm max-w-sm'}
            >
                {t(sentenceKey)}
            </Text>
        </div>
    );
};

/**
 * What the card shows for an employee with no completed cycle: the shape a trend takes, veiled,
 * with its caption above the veil at full contrast.
 *
 * Four things keep it honest, and all four are load-bearing: the scores come from a local constant,
 * the plot is hidden from assistive technology, the caption sits outside the veil, and no figure
 * appears anywhere -- neither on a mark nor on an axis.
 */
const TrendExample = () => {
    const { t } = useAmelioTranslations();

    const points: LineChartPoint[] = EXAMPLE_CYCLES.map(({ score, labelKey }, index) => ({
        id: `example-${index}`,
        value: score,
        label: t(labelKey),
    }));

    return (
        <div className={'relative'}>
            <div
                aria-hidden
                className={'opacity-30'}
                data-testid={'performance-trend-example-veil'}
            >
                <LineChart
                    points={points}
                    scale={TREND_SCORE_SCALE}
                    ariaLabel={t('performanceProfile.performanceTrend.example.badge')}
                    dataTestId={'performance-trend-example-chart'}
                />
            </div>

            <TrendNotice
                badgeKey={'performanceProfile.performanceTrend.example.badge'}
                sentenceKey={'performanceProfile.performanceTrend.example.sentence'}
                className={'absolute inset-0'}
            />
        </div>
    );
};

/**
 * The "Tendance de performance" card of the assessment tab: one mark per evaluation cycle, in the
 * order the cycles ran, each named by the level its score reaches.
 *
 * It reads its own data, like the panels beside it, so waiting for the server and failing to reach
 * it are states of their own -- neither may read as "this employee has no trend". A disabled query
 * is `pending` in TanStack Query v5 and not `loading`: a card that has not asked yet knows nothing,
 * and waits rather than drawing the example under someone's name.
 */
export const PerformanceTrendChart = ({ employeeId, className }: PerformanceTrendChartProps) => {
    const { t, i18n } = useAmelioTranslations();
    const trend = useGetPerformanceTrend(employeeId);
    const levels = useGetPerformanceScoreLevels();
    const { data: periods } = useGetEvaluationPeriods();

    if (trend.isPending || levels.isPending) {
        return (
            <TrendCard className={className}>
                <div
                    className={'flex justify-center'}
                    data-testid={'performance-trend-loading'}
                    aria-busy
                >
                    <Spinner
                        size={'md'}
                        color={Colors.PRIMARY}
                    />
                </div>
            </TrendCard>
        );
    }

    if (trend.isError || levels.isError) {
        return (
            <TrendCard className={className}>
                <Text
                    color={Colors.ERROR}
                    align={'center'}
                    className={'text-body-sm'}
                >
                    {t('performanceProfile.error')}
                </Text>
            </TrendCard>
        );
    }

    if (trend.data.points.length === 0) {
        // An employee whose every cycle predates the span has been evaluated, so the example --
        // which says a trend "will appear" -- would deny something that happened.
        return (
            <TrendCard className={className}>
                {trend.data.hasCyclesBeforeWindow ? (
                    <TrendNotice
                        badgeKey={'performanceProfile.performanceTrend.beforeWindow.badge'}
                        sentenceKey={'performanceProfile.performanceTrend.beforeWindow.sentence'}
                    />
                ) : (
                    <TrendExample />
                )}
            </TrendCard>
        );
    }

    const chartPoints = toTrendChartPoints({
        points: trend.data.points,
        periods,
        language: i18n.language,
        nextCycleLabel: t('performanceProfile.performanceTrend.nextCycle'),
    });

    return (
        <TrendCard className={className}>
            {/* The curve wears the violet the dial beside it already draws its arc in: on this
                screen that color means "this person's performance", and the two tiles say it
                together. The chart paints in `currentColor`, so the token decides. */}
            <div className={'text-growth-violet h-full min-h-0'}>
                <LevelTrendChart
                    points={chartPoints}
                    bands={toTrendBands(levels.data)}
                    scale={TREND_SCORE_SCALE}
                    reachedBandId={toReachedBandId(trend.data.points, levels.data)}
                    reachedBandLabelByPointId={toReachedLevelLabels({
                        points: trend.data.points,
                        chartPoints,
                        levels: levels.data,
                    })}
                    ariaLabel={t('performanceProfile.performanceTrend.chartLabel')}
                    dataTestId={'performance-trend-chart'}
                />
            </div>
        </TrendCard>
    );
};
